import type es from '../es/english';

/** `english` in English. Same keys as `es/english.ts`, enforced by the type. */
const english: typeof es = {
  status: {
    requested: 'Requested',
    notified: 'Notified',
    in_call: 'On the call',
    scoring: 'Scoring',
    completed: 'Ready',
    expired: 'Expired',
    canceled: 'Canceled',
    failed: 'Error',
  },

  report: {
    level: 'English level',
    insufficient: 'Not enough evidence',
    insufficientNote:
      'The English conversation was too short to assign a level. At least three minutes of English speech are needed.',
    confidence: { low: 'Low confidence', medium: 'Medium confidence', high: 'High confidence' },
    dimensions: {
      fluency: 'Fluency',
      grammar: 'Grammar',
      vocabulary: 'Vocabulary',
      comprehension: 'Comprehension',
      pronunciation: 'Pronunciation',
    },
    pronunciationPending: 'Not measured yet',
    pronunciationNotMeasured: 'We could not measure it on this call',
    pronunciationIssues: 'To practice',
    issueAt: (at: string) => `at ${at}`,
    recording: 'Call recording',
    listen: 'Listen to the call',
    recordingLoading: 'Opening the recording',
    recordingFailed: 'We could not open the recording. Try again.',
    recordingPrivacy: 'Private: only the employer who requested the assessment and the candidate can listen.',
    recordingUntil: (date: string) => `Available until ${date}.`,
    evidence: 'What they said',
    transcript: 'Show the transcript',
    hideTranscript: 'Hide the transcript',
    interviewer: 'Interviewer',
    candidate: 'Candidate',
    scoredOn: (date: string) => `Scored on ${date}`,
    reused: 'Result from a recent assessment of this person: no need to repeat the call.',
    disclaimer:
      'This is an AI pre-screening based on the conversation. Use it together with your own interview.',
  },

  agency: {
    title: 'English',
    request: 'Assess English',
    confirmTitle: 'Assess their English?',
    confirmBody:
      'We ask the candidate to call TBit Jobs on WhatsApp. An AI interviewer talks with them in English for about 10 minutes. The call is recorded and both you and the candidate see the result.',
    confirmDeadline: 'They have 7 days to call. You never see their number.',
    confirm: 'Yes, request it',
    cancel: 'Not now',
    cancelRequest: 'Cancel the assessment',
    loading: 'Loading the assessment',
    requestFailed: 'We could not request the assessment. Try again.',
    cancelFailed: 'We could not cancel it.',
    waiting: (date: string) => `We notified the candidate. They can call until ${date}.`,
    inCall: 'The candidate is on the call right now.',
    scoring: 'The call ended. We are scoring it, it takes a couple of minutes.',
    retryChip: 'Waiting for another try',
    shortCallChip: 'Short call, waiting for another try',
    shortCall: (left: number) =>
      left === 1
        ? 'The last call was too short to score. They have 1 attempt left.'
        : `The last call was too short to score. They have ${left} attempts left.`,
    expired: 'It expired without a complete call. You can request it again.',
    canceled: 'You canceled it. You can request it again.',
    failed: 'We could not score the call. We are looking into it.',
    askAgain: 'Request again',
    refresh: 'Refresh',
  },

  candidate: {
    cardTitle: (company: string) => `${company} wants to know your English level`,
    cardTitleFallback: 'A company wants to know your English level',
    forPosting: (title: string) => `For the ${title} job.`,
    howItWorks:
      'It is a short WhatsApp call with an AI interviewer from TBit. It takes about 10 minutes, the conversation is in English and it is recorded so it can be scored. Both the company and you see the result.',
    free: 'The call is free.',
    deadline: (date: string) => `You have until ${date}.`,
    tips: 'Find a quiet place. Take your time and answer in full sentences: there are no wrong answers.',
    phoneLabel: 'Your WhatsApp number',
    phoneHelp: 'Call from this number: that is how we know it is you.',
    phoneMissing: 'Before calling, tell us your WhatsApp number.',
    phoneCountry: 'Country',
    phoneNumber: 'Number',
    phonePlaceholder: '300 123 4567',
    phoneSave: 'Save number',
    phoneEdit: 'Change',
    phoneConfirmQuestion: 'Is this your WhatsApp? Confirm it before calling.',
    phoneConfirm: 'Yes, this one',
    phoneSaved: 'Done, we saved your number.',
    phoneInvalid: 'Check the number: write it without the country code.',
    phoneFailed: 'We could not save your number. Try again.',
    call: 'Call on WhatsApp',
    callFallback: (number: string) =>
      `If the button does not open the call (for example on a computer), open WhatsApp on your phone and call ${number}.`,
    callFirst: 'Save your WhatsApp number first.',
    inCall: 'You are on the call. When you hang up, we score your English.',
    scoring: 'Thanks for calling. We are scoring your English, it takes a couple of minutes.',
    shortCall: (left: number) =>
      left === 1
        ? 'Your last call was too short to give you a level. You have 1 attempt left: call again and talk a bit more.'
        : `Your last call was too short to give you a level. You have ${left} attempts left: call again and talk a bit more.`,
    resultTitle: 'Your English result',
    resultFor: (company: string, title: string) => `For ${company} · ${title}`,
    sharedWith: (company: string) => `${company} also sees this result.`,
    seeAll: 'See my English assessments',
    pageTitle: 'Your English assessments',
    emptyTitle: 'You have no English assessments yet',
    emptyNote: 'When a company wants to know your English level, we let you know here and by email.',
    pastTitle: 'Past assessments',
    expiredNote: 'It expired without a complete call.',
    canceledNote: 'The company canceled it.',
    failedNote: 'We could not score the call. We will write to you as soon as we fix it.',
    loadError: 'We could not load your English assessments. Reload the page.',
  },

  admin: {
    title: 'English assessments',
    byStatus: 'By status',
    recent: 'Last 20',
    cost: (usd: string) => `Total cost: US$${usd}`,
    empty: 'No assessments yet.',
    columns: {
      date: 'Date',
      agency: 'Agency',
      posting: 'Job',
      candidate: 'Candidate',
      phone: 'WhatsApp',
      status: 'Status',
      calls: 'Calls',
      level: 'Level',
    },
    reused: 'reused',
    shared: 'shared',
    demo: 'demo',
    rescore: 'Score again',
    rescoreFailed: 'Could not score it again.',
  },
};

export default english;
